import type React from 'react';
import { Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WithTooltip } from '@/components/with-tooltip';
import { cx } from 'class-variance-authority';
import { isNumber } from 'lodash';
import { HelpIconButton } from '@/components/help-panel/help-icon-button';
import { DataWarningIcon } from '@/components/metrics/data-warning-icon';
import type { DataSufficiencyState } from '@/components/metrics/metric-sections.types';

export interface MetricCardProps {
  title: string;
  value: number | string;
  currency?: string;
  icon?: React.ReactNode;
  description: string;
  isLoading: boolean;
  comparisonPercentage?: number | null;
  /** Inline beside the headline value (e.g. formatted %); uses foreground color, not the repurchase revenue accent */
  secondaryAffix?: React.ReactNode;
  /** Optional secondary value shown next to the main value (e.g. revenue in green) */
  secondaryValue?: number | null;
  secondaryValueTooltip?: string;
  secondaryValueCurrency?: string;
  helpKey?: string;
  helpIconId?: string;
  dataSufficiency?: DataSufficiencyState;
  isLoadingDataSufficiency?: boolean;
  /** Optional content aligned to the right of the headline value row (e.g. a small chart). */
  rightSlot?: React.ReactNode;
  /** Under the value: the variation against the previous period (CMP-01). */
  delta?: React.ReactNode;
}

export function MetricCard({
  title,
  value,
  currency,
  icon,
  description,
  isLoading,
  comparisonPercentage,
  secondaryAffix,
  secondaryValue,
  secondaryValueTooltip,
  secondaryValueCurrency,
  helpKey,
  helpIconId,
  dataSufficiency,
  isLoadingDataSufficiency = false,
  rightSlot,
  delta,
}: MetricCardProps) {
  const { LL, locale } = useI18nContext();
  const isDataUnavailable = Boolean(
    dataSufficiency && !dataSufficiency.canDisplay
  );

  const formatWithCurrency = () => {
    if (isNumber(value)) {
      const formattedValue = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency ?? 'USD',
      }).format(value);

      return formattedValue;
    }
  };

  const formatPlainNumber = (n: number) =>
    new Intl.NumberFormat(locale).format(n);

  const formatComparison = () => {
    if (comparisonPercentage === null || comparisonPercentage === undefined) {
      return null;
    }

    const isPositive = comparisonPercentage >= 0;
    const formattedPercentage = `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(comparisonPercentage)}%`;

    return (
      <WithTooltip
        tooltip={secondaryValueTooltip ?? ''}
        className="max-w-[320px] text-sm font-normal"
      >
        <span
          className={cx(
            'text-sm font-medium flex items-center gap-1 cursor-help',
            {
              'text-green-500': isPositive,
              'text-blue-400': !isPositive,
            }
          )}
        >
          {isPositive ? (
            <ArrowUp className="h-3 w-3" strokeWidth={4} />
          ) : (
            <ArrowDown className="h-3 w-3" strokeWidth={4} />
          )}
          {formattedPercentage}
        </span>
      </WithTooltip>
    );
  };

  const formatSecondaryValue = () => {
    if (
      secondaryValue === null ||
      secondaryValue === undefined ||
      secondaryValueCurrency === undefined
    ) {
      return null;
    }

    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: secondaryValueCurrency,
    }).format(secondaryValue);

    return (
      <span className="text-sm font-medium text-green-500">{formatted}</span>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-1.5">
          <span>{title}</span>
          {helpKey ? (
            <HelpIconButton id={helpIconId} helpKey={helpKey} />
          ) : null}
          {dataSufficiency?.shouldShowWarning ? (
            <DataWarningIcon
              version={dataSufficiency.minAppVersion}
              percentage={Math.round(
                dataSufficiency.activeUsersOnOrAfterVersion.percentage * 100
              )}
            />
          ) : null}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="flex flex-row items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="text-2xl font-bold flex flex-wrap items-center gap-2">
              {isLoading || isLoadingDataSufficiency ? (
                <Loader2 className="animate-spin" />
              ) : isDataUnavailable ? (
                '--'
              ) : (
                <>
                  {currency && isNumber(value)
                    ? formatWithCurrency()
                    : isNumber(value)
                      ? formatPlainNumber(value)
                      : value}
                  {!isLoading && secondaryAffix != null ? (
                    <span className="text-sm font-medium text-foreground">
                      {secondaryAffix}
                    </span>
                  ) : null}
                  {!isLoading && formatComparison()}
                  {!isLoading && formatSecondaryValue()}
                </>
              )}
            </div>
          </div>
          {rightSlot ? (
            <div className="flex flex-1 items-center justify-center">
              <div className="flex shrink-0 self-center">{rightSlot}</div>
            </div>
          ) : null}
        </div>
        {delta && !isLoading && !isDataUnavailable ? (
          <div className="mb-1">{delta}</div>
        ) : null}
        <p
          className={cx('text-xs text-muted-foreground', {
            'opacity-0': isLoading || isLoadingDataSufficiency,
          })}
        >
          {isDataUnavailable ? LL.metricsPage.warnings.noData() : description}
        </p>
      </CardContent>
    </Card>
  );
}
